import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaTimes, FaBars } from "react-icons/fa";

import '../styles/Navbar.css';

function Navbar({ children }) {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');
  const [activeSubMenu, setActiveSubMenu] = useState('');
  const [mobileExpanded, setMobileExpanded] = useState('');
  const [mobileSubExpanded, setMobileSubExpanded] = useState('');
  const location = useLocation();
  const navRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setActiveMenu('');
    setActiveSubMenu('');
    setDrawerOpen(false);
    setMobileExpanded('');
    setMobileSubExpanded('');
  }, [location.pathname]);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveMenu('');
        setActiveSubMenu('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isDrawerOpen]);

  const navItems = [
    { label: 'Home', to: '/' },
    {
      label: 'About',
      children: [
        { label: 'Mission, Vision & History', to: '/about/mission' },
        { label: 'Board of Directors', to: '/about/board' },
        { label: 'Secretariat', to: '/about/secretariat' },
      ]
    },
    {
      label: 'Membership',
      children: [
        { label: 'Our Members', to: '/membership/listing' },
        { label: 'Membership Categories', to: '/membership/categories' },
        { label: 'Membership Benefits', to: '/membership/benefits' },
        { label: 'CANTO Working Committees', to: '/membership/working-committees' },
        { label: 'Exclusive Offers for CANTO Members', to: '/membership/exclusive-offers' },
        { label: 'Membership FAQs', to: '/membership/faqs' },
      ]
    },
    {
      label: 'Events',
      children: [
        {
          label: 'CANTO Connect', to: '/events/canto-connect/2',
          children: [
            { label: 'AGM Documents', to: '/events/agm-documents' },
          ]
        },
        {
          label: 'CANTO Annual Conference & Trade Exhibition', to: '/events/canto-conference',
          children: [
            { label: 'Call for Participation', to: '/events/call-for-participation' },
          ]
        },
        {
          label: 'Competitions', to: '/events/innovation-challenge',
          children: [
            { label: 'Innovation Challenge', to: '/events/innovation-challenge' },
            { label: 'Ministerial Award in Innovation', to: '/events/ministerial-award' },
          ]
        },
        { label: 'CANTO Conversations', to: '/events/canto-conversations' },
        { label: 'Event Calendar', to: '/events/calendar' },
      ]
    },
    {
      label: 'Publications',
      children: [
        { label: 'Press Releases', to: '/publications/press-releases' },
        { label: 'CANCION Magazine', to: '/publications/cancion' },
        { label: 'CANTO Publications', to: '/publications/canto-publications' },
        { label: 'White Papers', to: '/publications/white-papers' },
      ]
    },
    { label: 'E-Market', to: '/emarket' },
    { label: 'Contact Us', to: '/contact' },
  ];

  const toggleMobileExpanded = (label) => {
    setMobileExpanded(mobileExpanded === label ? '' : label);
    setMobileSubExpanded('');
  };

  const toggleMobileSubExpanded = (label) => {
    setMobileSubExpanded(mobileSubExpanded === label ? '' : label);
  };

  const closeAll = () => {
    setDrawerOpen(false);
    setMobileExpanded('');
    setMobileSubExpanded('');
  };

  return (
    <>
      <nav className='navbar' ref={navRef}>
        <Link className='navbar-logo-container' to={'/'}>
          {location.pathname === '/emarket' ? (
            <img src={process.env.PUBLIC_URL + '/assets/emarket_logo_white.png'} className='navbar-logo' alt='E-Market Logo' style={{height: '65px'}}/>
          ) : (
            <img src={process.env.PUBLIC_URL + '/assets/nav_logo.png'} className='navbar-logo' alt='CANTO Logo'/>
          )}
        </Link>

        {/* Desktop Navigation */}
        <div className='navbar-desktop'>
          {navItems.map((item) => (
            item.children ? (
              <div
                key={item.label}
                className={`navbar-item has-dropdown ${activeMenu === item.label ? 'active' : ''}`}
                onClick={() => setActiveMenu(activeMenu === item.label ? '' : item.label)}
              >
                <span className='navbar-item-label'>
                  {item.label}
                  <FaChevronDown size={10} className='navbar-item-chevron' />
                </span>
                {activeMenu === item.label && (
                  <div className='navbar-dropdown'>
                    {item.children.map((child) => (
                      child.children ? (
                        <div
                          key={child.label}
                          className='navbar-dropdown-item has-sub'
                          onMouseEnter={() => setActiveSubMenu(child.label)}
                          onMouseLeave={() => setActiveSubMenu('')}
                        >
                          <Link to={child.to} className='navbar-dropdown-link'>
                            {child.label}
                            <FaChevronRight size={10} />
                          </Link>
                          {activeSubMenu === child.label && (
                            <div className='navbar-subdropdown'>
                              {child.children.map((sub) => (
                                <Link key={sub.label} to={sub.to} className='navbar-dropdown-link'>
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link key={child.label} to={child.to} className='navbar-dropdown-link'>
                          {child.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.label} className='navbar-item' to={item.to}>
                <span className='navbar-item-label'>{item.label}</span>
              </Link>
            )
          ))}
        </div>

        {/* Mobile Hamburger Button */}
        <button className='navbar-hamburger' onClick={() => setDrawerOpen(true)} aria-label='Open menu'>
          <FaBars size={22} />
        </button>
      </nav>

      {/* Mobile Drawer Overlay */}
      <div className={`navbar-drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={closeAll} />

      {/* Mobile Drawer */}
      <div className={`navbar-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className='navbar-drawer-header'>
          <img src={process.env.PUBLIC_URL + '/assets/nav_logo.png'} className='navbar-drawer-logo' alt='CANTO Logo'/>
          <button className='navbar-drawer-close' onClick={closeAll} aria-label='Close menu'>
            <FaTimes size={20} />
          </button>
        </div>

        <div className='navbar-drawer-items'>
          {navItems.map((item) => (
            item.children ? (
              <div key={item.label} className='navbar-drawer-group'>
                <div
                  className={`navbar-drawer-item parent ${mobileExpanded === item.label ? 'expanded' : ''}`}
                  onClick={() => toggleMobileExpanded(item.label)}
                >
                  <span>{item.label}</span>
                  <FaChevronDown size={12} className={`navbar-drawer-chevron ${mobileExpanded === item.label ? 'rotated' : ''}`} />
                </div>
                {mobileExpanded === item.label && (
                  <div className='navbar-drawer-children'>
                    {item.children.map((child) => (
                      child.children ? (
                        <div key={child.label} className='navbar-drawer-subgroup'>
                          <Link to={child.to} className='navbar-drawer-child' onClick={closeAll}>
                            {child.label}
                          </Link>
                          <button
                            className='navbar-drawer-sub-toggle'
                            onClick={(e) => { e.preventDefault(); toggleMobileSubExpanded(child.label); }}
                          >
                            <FaChevronDown size={10} className={`navbar-drawer-chevron ${mobileSubExpanded === child.label ? 'rotated' : ''}`} />
                          </button>
                          {mobileSubExpanded === child.label && (
                            <div className='navbar-drawer-subchildren'>
                              {child.children.map((sub) => (
                                <Link key={sub.label} to={sub.to} className='navbar-drawer-subchild' onClick={closeAll}>
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link key={child.label} to={child.to} className='navbar-drawer-child' onClick={closeAll}>
                          {child.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.label} to={item.to} className='navbar-drawer-item' onClick={closeAll}>
                <span>{item.label}</span>
              </Link>
            )
          ))}
        </div>
      </div>
    </>
  );
}

export default Navbar;
